import { WidgetEnv } from "@xapp/stentor-chat-widget";
import React, { FC, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { ChatConfigContext, Config, defaultServerUrl, useConnectionInfo } from "../../config";
import { useOpenUrlCallback } from "../../hooks/useOpenUrlCallback";
import { MessageMiddleware } from "../../middlewares";
import { executeAction, reset, sendFile, sendMessage, sendTyping } from "../../store/actions";
import { writeMessage } from "../../store/actions/writeMessage";
import { CONNECTION_STATUS_TYPE } from "../../store/ChatAction";
import { ChatState } from "../../store/ChatState";
import { checkSessionExpiration } from "../../utils/checkSessionExpiration";
import { getConfigurableMessages } from "../../utils/configurableMessages";
import { useChatDispatch } from "../../utils/useChatDispatch";
import { useChatServerDispatch } from "../../utils/useChatServerDispatch";
import useExternalScript from "../../utils/useExternalScript";
import useGreeting from "../../utils/useGreeting";
import { ChatServerOptions } from "../../xapp/ChatServer";
import { ChatMessageRequest } from "../../xapp/ChatServerMessage";
import { ChatServerContext, useChatServer } from "../../xapp/XappChat";
import { ChatButton } from "../ChatButton";
import { ChatFooter } from "../ChatFooter";
import { MessageList } from "../MessageList";
import { ServerOffline } from "../ServerOffline";
import { ChatHeader } from "../ChatHeader";
import { WidgetStylesheet } from "../WidgetStylesheet";
import "./ChatWidget.scss";
import RefreshConfirmationModal from "./ModalContent";
import setVisualStatus from "../../store/actions/setVisualStatus";
import setSessionId from "../../store/actions/setSessionId";
import { log } from "../../utils";

export interface ChatWidgetProps {
    readonly preChatFormEnabled?: boolean;
    readonly config?: WidgetEnv;
    readonly mode?: ChatWidgetMode;
    readonly messageMiddleware?: MessageMiddleware;
    onConnectionStatusChange?(status: CONNECTION_STATUS_TYPE): void;
}

export interface ChatWidgetWrapperProps extends ChatWidgetProps { }

export type ChatWidgetMode = "normal" | "docked" | "static";

const ChatWidgetWrapper: FC<ChatWidgetWrapperProps> = (props) => {
    const rawConfig = props.config;
    const connection = useConnectionInfo(rawConfig);

    const config = useMemo<Config>(
        () => ({
            ...rawConfig,
            connection,
            assetUrl: connection?.serverUrl ?? defaultServerUrl,
            env: rawConfig
        }),
        [connection, rawConfig]
    );

    const token = useSelector<ChatState, string | null>((state) => state.connection.token);

    const options = useMemo<ChatServerOptions>(() => {
        let configurableMessages = getConfigurableMessages();

        if (
            rawConfig.configurableMessages &&
            Array.isArray(rawConfig.configurableMessages.items) &&
            rawConfig.configurableMessages.items.length > 0
        ) {
            configurableMessages = rawConfig.configurableMessages;
        }

        return {
            token,
            bot: {
                nick: "Bot",
                displayName: rawConfig.botName,
                avatarPath: rawConfig.avatarUrl
            },
            configurableMessages,
            hooks: rawConfig.hooks
        };
    }, [token, rawConfig]);

    const chatServer = useChatServer(connection, options);

    return (
        <ChatConfigContext.Provider value={config}>
            <ChatServerContext.Provider value={chatServer}>
                <ChatWidget {...props} />
            </ChatServerContext.Provider>
        </ChatConfigContext.Provider>
    );
};

const ChatWidget: FC<ChatWidgetProps> = (props) => {
    const innerDispatch = useChatDispatch();
    const dispatch = useChatServerDispatch();

    // From Redux
    const chatState = useSelector<ChatState, ChatState>((state) => state);

    // Refresh modalReference
    const modalRef = useRef<HTMLDivElement>({} as HTMLDivElement);

    const mode = props.mode ?? "normal";
    const dockedMode = mode === "docked";
    const staticMode = mode === "static";
    const modeClass = `widget-container--${mode}`;

    const canRefresh = props.config.header?.actions?.refresh;
    // can't minimize in docked mode or static mode.
    const canMinimize = !dockedMode && !staticMode && props.config.header?.actions?.minimize;
    log(`docked: ${dockedMode} static: ${staticMode} minimimized: ${props.config.header?.actions?.minimize}`);

    let canCancel: boolean;
    // To preserve legacy behavior, cancel needs a little more checks
    if (typeof props.config.header?.actions?.cancel === "boolean") {
        canCancel = !dockedMode && !staticMode && props.config.header?.actions?.cancel;
    } else {
        canCancel = !dockedMode && !staticMode;
    }

    // For backward compatibility. Note: the action will create the actual "visuals" object" (this is a copy). 
    if (!chatState.visuals) {
        chatState.visuals = {};
    }

    // Our state - pull from storage
    const [visible, setVisibleState] = useState<boolean>(
        (!canMinimize && !canCancel) ||
        // !!get("visible") ||
        chatState.visuals.visible ||
        (props.config?.autoOpenOnWidth &&
            window.matchMedia(`(min-width: ${props.config?.autoOpenOnWidth})`).matches)
    );
    const [typing, setTypingState] = useState<boolean>(false); // false initially

    const chatServer = useContext(ChatServerContext);

    const patternsConfig = props.config?.autoOpenOnPattern?.patterns;

    const currentUrl = window.location.href;

    const patternExist = patternsConfig && patternsConfig.length > 0;

    const patternMatches = patternsConfig?.some((pattern) => currentUrl.includes(pattern));

    const configWidth = props.config?.autoOpenOnPattern?.minimumWidth;

    // eslint-disable-next-line no-restricted-globals
    const currentWidth = screen.width;

    const setVisible = useCallback(
        (newVisible: boolean) => {
            if (staticMode) {
                return;
            }
            setVisibleState(newVisible);
            innerDispatch(setVisualStatus({
                visible: newVisible
            }));
        },
        [innerDispatch, staticMode]
    );

    useEffect(() => {
        document.addEventListener("keydown", function (event) {
            const body = document.getElementsByTagName("body")[0];
            body.tabIndex = -1;
            if (event.key === "Escape") {
                body.focus();
            }
        });
        if (
            checkSessionExpiration(
                chatState?.sessionExpiration || props.config?.sessionExpiration,
                chatState?.chats[chatState.chats.length - 1]?.timestamp,
                chatState?.lastTimestamp
            )
        ) {
            innerDispatch(setSessionId(undefined));
            innerDispatch(reset());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [isTabVisible, setIsTabVisible] = useState(!document.hidden);

    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsTabVisible(!document.hidden);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        // Too early?
        if (!chatServer) {
            return;
        }

        if (!isTabVisible) {
            chatServer.sleep();
        } else {
            chatServer.wakeup();
        }
    }, [chatServer, innerDispatch, isTabVisible]);

    useEffect(() => {
        // For reopen widget after move on same window
        // if (get("opened")) {
        //     setVisible(true);
        // }

        if (chatState.visuals.opened) {
            setVisible(true);
        } else {
            if (mode === "normal") {
                setVisible(false);
            }
        }

        if (patternExist && patternMatches) {
            setVisible(true)
        }

        if (currentWidth < +configWidth) {
            setVisible(false)
        }

    }, [currentWidth, patternExist, patternMatches, configWidth, setVisible, chatState.visuals.opened, mode]);

    function handleOnChange() {
        if (!typing) {
            dispatch(sendTyping(true));
            setTypingState(true);
        }

        stopTyping();
    }

    function stopTyping() {
        if (!typing) return;
        dispatch(sendTyping(false));
        setTypingState(false);
    }

    function handleSendMessage(msg: ChatMessageRequest) {
        dispatch(sendMessage(msg));
    }

    function handleWriteMessage(msg: ChatMessageRequest) {
        innerDispatch(writeMessage(msg.msg, msg.user));
    }

    const handleOpenUrl = useOpenUrlCallback();

    function handleOnSubmit(rawInput: string) {
        // Don't allow visitor to send msg if not chatting
        if (chatState.accountStatus === "offline" && !chatState.isChatting) return;

        // Don't send empty messages
        if (!rawInput) return;

        // Immediately stop typing
        stopTyping();

        chatServer.flush();

        dispatch(executeAction(rawInput));
    }

    function handleFileUpload(event: React.DragEvent<HTMLInputElement>) {
        event.preventDefault();

        // Only send the first file dropped on input
        const file = event?.dataTransfer?.files[0];

        if (!file) {
            return;
        }

        dispatch(sendFile(file));
    }

    function getVisibilityClass() {
        return visible ? "visible" : "";
    }

    function openRestartConfirmationModal() {
        // Interesting, this call the modalRef of the restart modal
        modalRef.current.style.display = "block";
    }

    function closeRestartConfirmationModal() {
        modalRef.current.style.display = "none";
    }

    function handleReset() {
        innerDispatch(reset());
    }

    function handleRestartClick() {
        if (config.header.actions.refreshShowConfirmation) {
            // show the modal
            openRestartConfirmationModal();
            // we will close when
        } else {
            handleReset()
        }
    }

    function handleRestartModalCloseClick() {
        closeRestartConfirmationModal();
    }

    /** Called when minimize button is clicked */
    function handleMinimizeClick() {
        innerDispatch(setVisualStatus({
            opened: false
        }));
        setVisible(false);
    }

    /** Called when cancel is clicked */
    function handleCancelClick() {
        //set("opened", false);
        innerDispatch(setVisualStatus({
            opened: false
        }));
        innerDispatch(reset());
        setVisible(false);
    }

    function chatButtonOnClick() {
        innerDispatch(setVisualStatus({
            opened: true
        }));
        setVisible(true);
        setTimeout(() => {
            document.getElementById("chatWidgetInput").focus();
        }, 100);
    }

    const isOffline = chatState.accountStatus === "offline" && !chatState.isChatting;
    const messages = chatState && chatState.chats;

    const { config, onConnectionStatusChange } = props;

    useGreeting(!isOffline && !props.preChatFormEnabled && visible);

    const connectionStatus = chatState.connection.connectionStatus;
    useEffect(() => {
        if (onConnectionStatusChange) {
            onConnectionStatusChange(connectionStatus);
        }
    }, [connectionStatus, onConnectionStatusChange]);

    useExternalScript(props.config?.middlewareUrl);

    // This is a pseudo agent. It represent's the widget (shown in the header avatar for instance)
    const widgetAgent = chatState.agents["agent:robot"]?.user || config?.agent
        || { nick: "agent:robot", avatarPath: config.avatarUrl, display_name: "Agent" };

    return (
        <>
            <div className={`widget-container ${modeClass} ${getVisibilityClass()}`}>
                <WidgetStylesheet theme={config?.theme} />
                <ChatHeader
                    accountStatus={chatState.accountStatus}
                    refreshOnClick={handleRestartClick}
                    minimizeOnClick={handleMinimizeClick}
                    cancelOnClick={handleCancelClick}
                    agent={widgetAgent}
                    canRefresh={canRefresh}
                    canMinimize={canMinimize}
                    canCancel={canCancel}
                    config={config?.header}
                    menuConfig={config.menu}
                    onSubmit={handleOnSubmit}
                />
                <MessageList
                    visible={visible}
                    queuePosition={chatState.queuePosition}
                    isChatting={chatState.isChatting}
                    isOffline={isOffline}
                    messages={messages}
                    agents={chatState.agents}
                    agent={config?.agent}
                    lastRatingRequestTimestamp={chatState.lastRatingRequestTimestamp}
                    hasRating={chatState.hasRating}
                    visitorId={chatState.visitorId}
                    messageMiddleware={props.messageMiddleware}
                    textTypingStatusEnabled={props.config?.typingStatus?.textTypingStatusEnabled}
                    onSend={handleSendMessage}
                    onWrite={handleWriteMessage}
                    onOpenUrl={handleOpenUrl}
                    minimizeOnClick={handleMinimizeClick}
                />
                <div
                    className={`xa-spinner-container ${visible && connectionStatus === "pending" ? "visible" : ""
                        }`}>
                    <div className="xa-spinner" />
                </div>
                {connectionStatus === "offline" && <ServerOffline />}
                <ChatFooter
                    isAdmin={config?.isAdmin}
                    isChatting={chatState.isChatting}
                    placeholder={config?.input?.placeholder}
                    sendButtonIcon={config?.footer?.sendButton?.icon}
                    visible={visible}
                    menuConfig={props.config.menu}
                    footerConfig={props.config?.footer}
                    inputConfig={props.config?.input}
                    onChange={handleOnChange}
                    onSubmit={handleOnSubmit}
                    onFileUpload={handleFileUpload}
                />
                <div className="restartModal" ref={modalRef} onClick={handleRestartModalCloseClick}>
                    <RefreshConfirmationModal onClose={handleRestartModalCloseClick} onReset={handleReset} />
                </div>
            </div>
            <ChatButton
                addClass={getVisibilityClass()}
                onClick={chatButtonOnClick}
                config={config?.cta}
                imageUrl={config?.chatButton?.imageUrl}
                visible={visible}
            />
        </>
    );
};

export default ChatWidgetWrapper;
